import React from "react";
import { Edit2, Import, Receipt } from "iconsax-react";
import { CheckIcon, CloseIcon } from "@/shared/assets/icons";
import type { AdvanceStatus, AdvanceKind, AdvanceListType } from "../types";

export type AdvanceAction =
  | "approve"
  | "reject"
  | "request_revision"
  | "issue"
  | "settle"
  | "download_t3"
  | "sign";

export interface ActionConfig {
  id: AdvanceAction;
  label: string;
  variant: "primary" | "secondary" | "danger";
  icon: React.ReactNode;
}

export interface AdvanceIsLoadingState {
  isIssuing: boolean;
  isReviewing: boolean;
  isDownloadingT3: boolean;
  isSigning?: boolean;
  isSettling?: boolean;
}

export interface AdvanceActionsConfig {
  actions: ActionConfig[];
  isLoading: boolean;
}

interface AdvanceEntityInput {
  status: AdvanceStatus;
  type?: AdvanceListType;
  kind?: AdvanceKind;
  application_review_note: string | null;
}

export function getAvailableActions(
  entity: AdvanceEntityInput,
  isLoading: AdvanceIsLoadingState,
  t: (key: string) => string,
): AdvanceActionsConfig {
  const { status, type, kind } = entity;
  const actions: ActionConfig[] = [];

  const isAccountable = type === "trip" || kind === "employee";
  const hasRevisionNote = entity.application_review_note != null && entity.application_review_note !== "";
  const canReview = status === "pending" && !hasRevisionNote;
  const canIssue = type === "trip" ? status === "order_acknowledged" : status === "approved";
  const canSettle = status === "issued" && isAccountable;
  const canOrgSign = type !== "trip" && kind === "salary" && status === "signing_pending";
  const hasT3 = isAccountable && status === "settled";

  if (canReview) {
    actions.push({
      id: "approve",
      label: t("actions.approve"),
      variant: "primary",
      icon: React.createElement(CheckIcon),
    });
  }

  if (canReview) {
    actions.push({
      id: "request_revision",
      label: t("actions.requestRevision"),
      variant: "secondary",
      icon: React.createElement(Edit2, { size: 16, color: "currentColor" }),
    });
  }

  if (canReview) {
    actions.push({
      id: "reject",
      label: t("actions.reject"),
      variant: "danger",
      icon: React.createElement(CloseIcon),
    });
  }

  if (canIssue) {
    actions.push({
      id: "issue",
      label: t("actions.issue"),
      variant: "primary",
      icon: React.createElement(Import, { size: 16, color: "currentColor" }),
    });
  }

  if (canSettle) {
    actions.push({
      id: "settle",
      label: t("actions.settle"),
      variant: "primary",
      icon: React.createElement(Receipt, { size: 16, color: "currentColor" }),
    });
  }

  if (canOrgSign) {
    actions.push({
      id: "sign",
      label: t("actions.sign"),
      variant: "primary",
      icon: React.createElement(Edit2, { size: 16, color: "currentColor" }),
    });
  }

  if (hasT3) {
    actions.push({
      id: "download_t3",
      label: isLoading.isDownloadingT3 ? t("actions.downloading") : t("actions.downloadT3"),
      variant: "secondary",
      icon: React.createElement(Import, { size: 16, color: "currentColor" }),
    });
  }

  const isLoadingAny =
    isLoading.isIssuing ||
    isLoading.isReviewing ||
    isLoading.isDownloadingT3 ||
    !!isLoading.isSigning ||
    !!isLoading.isSettling;

  return {
    actions,
    isLoading: isLoadingAny,
  };
}
